import { useEffect, useRef, useState } from "react";
import { API_BASE } from "../api/client";
import { currentToken } from "../store/auth";
import type { GenRow, WsMessage } from "../api/types";

const WIRE_GENS = 200;
const HEARTBEAT_TIMEOUT_MS = 20_000; // server beats every 15s
const BACKOFF_MAX_MS = 30_000;

export type Conn = "connecting" | "live" | "stale" | "closed";

export interface RunStream {
  conn: Conn;
  status: string;
  fitnessDesc: string;
  bestSoFar: number | null;
  bestGen: number | null;
  genRows: GenRow[];
}

const EMPTY: RunStream = {
  conn: "connecting", status: "unknown", fitnessDesc: "",
  bestSoFar: null, bestGen: null, genRows: [],
};

function wsUrl(name: string): string {
  const proto = location.protocol === "https:" ? "wss" : "ws";
  const base = API_BASE || `${proto}://${location.host}`;
  const u = new URL(`${base}/api/v1/runs/${encodeURIComponent(name)}/ws`,
    base.startsWith("http") || base.startsWith("ws") ? undefined : location.origin);
  if (u.protocol === "http:") u.protocol = "ws:";
  if (u.protocol === "https:") u.protocol = "wss:";
  const token = currentToken();
  if (token) u.searchParams.set("token", token);
  return u.toString();
}

// Subscribe to one run's telemetry: hydrate from snapshot, apply deltas, reconnect
// with backoff (the server re-sends a fresh snapshot on reconnect, so no gaps).
export function useRunSocket(name: string): RunStream {
  const [state, setState] = useState<RunStream>(EMPTY);
  const stop = useRef(false);
  const sock = useRef<WebSocket | null>(null);
  const beat = useRef<number | undefined>(undefined);

  useEffect(() => {
    stop.current = false;
    let attempt = 0;
    setState(EMPTY);   // clear the previous run's state so the chip never shows it
    if (!name) return; // nothing selected yet



    const armWatchdog = () => {
      window.clearTimeout(beat.current);
      beat.current = window.setTimeout(
        () => setState((s) => ({ ...s, conn: "stale" })), HEARTBEAT_TIMEOUT_MS);
    };

    const connect = () => {
      if (stop.current) return;
      setState((s) => ({ ...s, conn: "connecting" }));
      const ws = new WebSocket(wsUrl(name));
      sock.current = ws;

      ws.onopen = () => { attempt = 0; armWatchdog(); };
      ws.onmessage = (ev) => {
        armWatchdog();
        const msg = JSON.parse(ev.data) as WsMessage;
        setState((s) => reduce(s, msg));
      };
      ws.onclose = () => {
        if (stop.current) return;
        setState((s) => ({ ...s, conn: "closed" }));
        const delay = Math.min(BACKOFF_MAX_MS, 1000 * 2 ** attempt++);
        window.setTimeout(connect, delay);
      };
      ws.onerror = () => ws.close();
    };

    connect();
    return () => {
      stop.current = true;
      window.clearTimeout(beat.current);
      sock.current?.close();
    };
  }, [name]);

  return state;
}

function reduce(s: RunStream, msg: WsMessage): RunStream {
  switch (msg.type) {
    case "snapshot":
      return {
        conn: "live", status: msg.status, fitnessDesc: msg.fitness_desc ?? "",
        bestSoFar: msg.best_so_far, bestGen: msg.best_gen,
        genRows: msg.gen_rows.slice(-WIRE_GENS),
      };
    case "gen": {
      const rows = [...s.genRows, msg.row].slice(-WIRE_GENS);
      const better = s.bestSoFar === null || msg.row.best_so_far > s.bestSoFar;
      return {
        ...s, conn: "live", genRows: rows,
        bestSoFar: better ? msg.row.best_so_far : s.bestSoFar,
        bestGen: better ? msg.row.gen : s.bestGen,
      };
    }
    case "config":
      return { ...s, conn: "live", fitnessDesc: msg.fitness_desc };
    case "run_started":
      return { ...s, conn: "live", status: "running" };
    case "run_finished":
      return { ...s, conn: "live", status: msg.status };
    case "heartbeat":
      return s.conn === "live" ? s : { ...s, conn: "live" };
    default:
      return s;
  }
}
